import { creatorbase } from '@round/api';
import { showNotification } from 'helpers';
import { useState } from 'react';
import useDeleteProjectDetailsPost from '../../hooks/posts/useDeleteProjectDetailsPost';
import { Modal, assets } from '@round/whitelabel-ui';
import styles from './DeletePostModal.module.css';
import { Button } from '@round/whitelabel-ui';
import cn from 'classnames';

const { ExternalLink: LinkIcon } = assets;

type Props = {
    isOpen: boolean;
    onClose: () => void;
    post: creatorbase.Post | null;
};

const DeletePostModal = ({ isOpen, onClose, post }: Props) => {
    const { deletePost } = useDeleteProjectDetailsPost(post);

    const [isDeleteLoading, setIsDeleteLoading] = useState(false);

    const handleDelete = async () => {
        if (!post) {
            return;
        }

        try {
            setIsDeleteLoading(true);
            const response = await deletePost(post.id);
            if (response.status === 204) {
                showNotification('Post deleted', 'info');
                onClose();
                return;
            }

            showNotification(response.data.detail || 'Could not delete post', 'error');
        } catch {
            showNotification('Could not delete post', 'error');
        } finally {
            setIsDeleteLoading(false);
        }
    };

    return (
        <Modal className={styles.modal} closeOnOverlayClick isOpen={isOpen} onClose={onClose}>
            <Modal.Header>
                <Modal.Title>Delete post</Modal.Title>
            </Modal.Header>
            <Modal.Body className={styles.body}>
                <p className={cn(styles.copy, styles.bold)}>You’re about to permanently delete the following:</p>
                <div className={styles.banner}>
                    <p className={styles.copy}>
                        <span className={styles.bold}>Post</span>:&nbsp;
                        {post && (
                            <a className={styles.link} href={post.post_url} target="_blank" rel="noreferrer">
                                {post.id.slice(-8)} <LinkIcon className={styles.linkIcon} />
                            </a>
                        )}
                    </p>
                </div>

                <p className={styles.copy}>You cannot restore posts. They are deleted permanently.</p>
            </Modal.Body>

            <Modal.Actions>
                <Button appearance="critical" isLoading={isDeleteLoading} onClick={handleDelete}>
                    Delete Post
                </Button>
                <Button appearance="ghost" onClick={onClose}>
                    Cancel
                </Button>
            </Modal.Actions>
        </Modal>
    );
};

export default DeletePostModal;
